/**
 * AufgabenPage — Microsoft To Do Style
 * 
 * DESIGN: Zweite Navi-Leiste links (wie bei Akten), Aufgabenliste Mitte, Detail-Panel rechts
 * 
 * Listen:
 * - Mein Tag (heute fällig + mir zugewiesen)
 * - Wichtig (Priorität hoch/dringend)
 * - Geplant (mit Fälligkeitsdatum)
 * - Mir zugewiesen (eigene Aufgaben)
 * - Alle Aufgaben
 * - Pro Betrieb (Gutachten, Autoglas, Mietwagen, Werkstatt)
 * 
 * Features:
 * - Inline Aufgabe erstellen
 * - Aufgabe als erledigt markieren (Checkbox)
 * - Detail-Panel mit Beschreibung, Checkliste, Zuweisung, Fälligkeitsdatum
 * - Mitarbeiter können sich selbst zuweisen
 * - Echtzeit-Updates via Supabase Realtime
 */

import { useState, useMemo, useCallback, useRef, useEffect } from 'react';
import { useAufgabenCRUD, type AufgabeMitMitarbeiter } from '@/hooks/useAufgabenCRUD';
import { useAufgabenDateien, useAufgabenKommentare, useAufgabenLabels, useAufgabeLabelZuordnung, useUnteraufgaben } from '@/hooks/useAufgabenExtras';
import type { AufgabeDatei, AufgabeKommentar, AufgabeLabel, Unteraufgabe } from '@/hooks/useAufgabenExtras';
import { useMitarbeiter } from '@/hooks/useSupabase';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/lib/supabase';
import Zeitstrahl from '@/components/fristen/Zeitstrahl';
import { BETRIEBE, type BetriebId } from '@/lib/betriebe';
import type { ChecklistItem, WiederholungTyp } from '@/lib/database.types';
import { WIEDERHOLUNG_LABELS, WOCHENTAG_LABELS, parseEmailMetadata } from '@/lib/database.types';
import { formatDatetimeLocalValue, parseDatetimeLocalToISO, validateFileUpload, MAX_FILE_SIZE } from '@/lib/aufgaben-utils';
import { DateTimePicker } from '@/components/ui/DateTimePicker';
import {
  CheckSquare, Plus, Sun, Star, CalendarDays, UserCheck, ListTodo,
  Building2, Circle, CheckCircle2, X, Trash2, Clock, Flag,
  ChevronRight, User, CalendarIcon, AlignLeft, ListChecks,
  SquarePen, Loader2, AlertCircle, Search, Filter, Users, UserPlus,
  ArrowUpDown, Repeat, RefreshCw, ArrowDown, ArrowUp,
  Paperclip, FileText, Image as ImageIcon, File, Download, MessageSquare,
  Send, Tag, Palette, MoreHorizontal, GripVertical, Subtitles, Cpu, Mail, ExternalLink,
  ChevronUp, ChevronDown, AtSign, Smile,
} from 'lucide-react';
import { toast } from 'sonner';
import { useMobileBackHandler } from '@/hooks/useMobileBackHandler';
import { showKiElements, KI_AGENT_ID } from '@/lib/kiAgent';

// ============================================================
// TYPEN
// ============================================================
type ListId = 'mein-tag' | 'wichtig' | 'geplant' | 'zugewiesen' | 'wiederkehrend' | 'alle' | BetriebId;

interface ListDef {
  id: ListId;
  label: string;
  icon: typeof Sun;
  color: string;
  separator?: boolean;
}

// ============================================================
// LISTEN-DEFINITIONEN
// ============================================================
const SMART_LISTS: ListDef[] = [
  { id: 'mein-tag', label: 'Mein Tag', icon: Sun, color: '#D97706' },
  { id: 'wichtig', label: 'Wichtig', icon: Star, color: '#DC2626' },
  { id: 'geplant', label: 'Geplant', icon: CalendarDays, color: '#059669' },
  { id: 'zugewiesen', label: 'Mir zugewiesen', icon: UserCheck, color: '#2563EB' },
  { id: 'wiederkehrend', label: 'Wiederkehrend', icon: Repeat, color: '#7C3AED' },
  { id: 'alle', label: 'Alle Aufgaben', icon: ListTodo, color: '#6B7280' },
];

// Alle Betrieb-Listen (inkl. adminOnly) — werden in der Komponente gefiltert
const ALL_BETRIEB_LISTS: (ListDef & { adminOnly?: boolean })[] = [
  { id: 'gutachten', label: 'Kfz Gutachten', icon: Building2, color: '#2563EB', separator: true },
  { id: 'autoglas', label: 'Autoglas', icon: Building2, color: '#059669' },
  { id: 'mietwagen', label: 'Mietwagen', icon: Building2, color: '#D97706' },
  { id: 'werkstatt', label: 'Werkstatt', icon: Building2, color: '#DC2626' },
  { id: 'protex', label: 'Protex / Amazon', icon: Building2, color: '#7C3AED', adminOnly: true },
  { id: 'unfallgiganten', label: 'Unfallgiganten', icon: Building2, color: '#0D9488', adminOnly: true },
  { id: 'unfallwiki', label: 'Unfallwiki', icon: Building2, color: '#0284C7', adminOnly: true },
  { id: 'airbnb', label: 'Airbnb / Immobilien', icon: Building2, color: '#B45309', adminOnly: true },
  { id: 'recht', label: 'Recht & Forderungen', icon: Building2, color: '#1D4ED8', adminOnly: true },
  { id: 'app_programmierung', label: 'APP Programmierung', icon: Building2, color: '#6B7280', adminOnly: true },
];

// ============================================================
// PRIORITÄTS-CONFIG
// ============================================================
const PRIORITAET_CONFIG: Record<string, { label: string; color: string; bg: string }> = {
  dringend: { label: 'Dringend', color: 'text-red-600', bg: 'bg-red-50 border-red-200' },
  hoch: { label: 'Hoch', color: 'text-orange-600', bg: 'bg-orange-50 border-orange-200' },
  mittel: { label: 'Mittel', color: 'text-blue-600', bg: 'bg-blue-50 border-blue-200' },
  niedrig: { label: 'Niedrig', color: 'text-gray-500', bg: 'bg-gray-50 border-gray-200' },
};

// ============================================================
// HAUPTKOMPONENTE
// ============================================================
export default function AufgabenPage() {
  const { aufgaben, loading, error, createAufgabe, updateAufgabe, toggleStatus, pendingStatusIds, assignToMe, unassign, removeAssignee, updateCheckliste, deleteAufgabe } = useAufgabenCRUD();
  const { data: mitarbeiterList } = useMitarbeiter();
  const { profile, impersonateUserId, teamMembers } = useAuth();
  const effectiveUserId = impersonateUserId || profile?.id || '';
  const isAdmin = profile?.rolle === 'admin';
  // WICHTIG: Wenn impersoniert wird, ist der effective User KEIN Admin (es sei denn der impersonierte User ist Admin)
  const impersonatedMember = impersonateUserId ? teamMembers.find(m => m.id === impersonateUserId) : null;
  // Robuste Logik: Wenn impersonateUserId gesetzt ist, MUSS der impersonierte User explizit Admin sein
  const effectiveIsAdmin = impersonateUserId 
    ? (impersonatedMember?.rolle === 'admin' ? true : false) 
    : isAdmin;

  // Betrieb-Listen: adminOnly nur für Admin sichtbar
  const BETRIEB_LISTS = useMemo(() => {
    return ALL_BETRIEB_LISTS.filter(list => !list.adminOnly || effectiveIsAdmin);
  }, [effectiveIsAdmin]);

  const ALL_LISTS = useMemo(() => {
    return [...SMART_LISTS, ...BETRIEB_LISTS];
  }, [BETRIEB_LISTS]);

  const [activeList, setActiveList] = useState<ListId>('mein-tag');
  const [selectedAufgabe, setSelectedAufgabe] = useState<AufgabeMitMitarbeiter | null>(null);
  const [showNewTask, setShowNewTask] = useState(false);

  // Mobile: Hardware-Back schließt Aufgaben-Detail statt Seite zu verlassen
  useMobileBackHandler(!!selectedAufgabe, () => setSelectedAufgabe(null));
  const [newTaskTitle, setNewTaskTitle] = useState('');
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => typeof window !== 'undefined' && window.innerWidth < 768);

  // Filter-States
  const [filterPrioritaet, setFilterPrioritaet] = useState<string>('');
  const [filterStatus, setFilterStatus] = useState<string>('');
  const [filterMitarbeiter, setFilterMitarbeiter] = useState<string>('');
  const [searchQuery, setSearchQuery] = useState('');
  
  // Sortier-State: 'faelligkeit-asc' (Standard), 'faelligkeit-desc', 'prioritaet', 'erstellt'
  const [sortierung, setSortierung] = useState<'faelligkeit-asc' | 'faelligkeit-desc' | 'prioritaet' | 'erstellt'>('faelligkeit-asc');

  // Aktuell ausgewählte Aufgabe aus der Liste holen (für Live-Updates)
  const currentAufgabe = useMemo(() => {
    if (!selectedAufgabe) return null;
    return aufgaben.find(a => a.id === selectedAufgabe.id) || null;
  }, [aufgaben, selectedAufgabe]);

  // Abteilungen/Betriebe des effektiven Users (impersonation-aware)
  const effectiveBusinessUnits = useMemo<string[]>(() => {
    if (impersonateUserId) return impersonatedMember?.business_units || [];
    return profile?.betriebe || [];
  }, [impersonateUserId, impersonatedMember, profile]);

  // ===== SICHTBARKEITS-FILTER: Mitarbeiter sehen zugewiesene/erstellte + eigene Abteilung =====
  const sichtbareAufgaben = useMemo(() => {
    if (!aufgaben) return [];
    if (effectiveIsAdmin) return aufgaben; // Admin sieht ALLE
    // Mitarbeiter: zugewiesene/erstellte Aufgaben ODER Aufgaben der eigenen Abteilung(en)
    return aufgaben.filter(a => {
      const istZugewiesen = a.zugewiesen_an === effectiveUserId ||
        (a.zugewiesen_an_liste || []).includes(effectiveUserId);
      const istErsteller = a.erstellt_von === effectiveUserId;
      const istEigeneAbteilung = !!a.business_unit && effectiveBusinessUnits.includes(a.business_unit);
      return istZugewiesen || istErsteller || istEigeneAbteilung;
    });
  }, [aufgaben, effectiveUserId, effectiveIsAdmin, effectiveBusinessUnits]);

  // ===== FILTER-LOGIK =====
  const filteredAufgaben = useMemo(() => {
    if (!sichtbareAufgaben) return [];
    const today = new Date().toISOString().split('T')[0];
    const myId = effectiveUserId;

    // Erst nach Liste filtern
    let result: AufgabeMitMitarbeiter[];
    switch (activeList) {
      case 'mein-tag':
        result = sichtbareAufgaben.filter(a => {
          const isToday = a.faellig_am && a.faellig_am.startsWith(today);
          const isMine = a.zugewiesen_an === myId || (a.zugewiesen_an_liste || []).includes(myId);
          return (isToday || isMine) && a.status !== 'erledigt';
        });
        break;
      case 'wichtig':
        result = sichtbareAufgaben.filter(a => (a.prioritaet === 'hoch' || a.prioritaet === 'dringend') && a.status !== 'erledigt');
        break;
      case 'geplant':
        result = sichtbareAufgaben.filter(a => a.faellig_am && a.status !== 'erledigt');
        break;
      case 'zugewiesen':
        result = sichtbareAufgaben.filter(a => {
          return (a.zugewiesen_an === myId || (a.zugewiesen_an_liste || []).includes(myId)) && a.status !== 'erledigt';
        });
        break;
      case 'wiederkehrend':
        result = sichtbareAufgaben.filter(a => a.wiederholung_typ && a.status !== 'erledigt');
        break;
      case 'alle':
        result = sichtbareAufgaben;
        break;
      default:
        result = sichtbareAufgaben.filter(a => a.business_unit === activeList);
    }

    // Zusatzfilter anwenden
    if (filterPrioritaet) {
      result = result.filter(a => a.prioritaet === filterPrioritaet);
    }
    if (filterStatus) {
      result = result.filter(a => a.status === filterStatus);
    }
    if (filterMitarbeiter) {
      result = result.filter(a => 
        a.zugewiesen_an === filterMitarbeiter || 
        (a.zugewiesen_an_liste || []).includes(filterMitarbeiter)
      );
    }
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      result = result.filter(a => 
        a.titel.toLowerCase().includes(q) || 
        (a.beschreibung || '').toLowerCase().includes(q)
      );
    }

    return result;
  }, [sichtbareAufgaben, activeList, effectiveUserId, filterPrioritaet, filterStatus, filterMitarbeiter, searchQuery]);

  // Sortierung: Konfigurierbar — Standard nach Fälligkeitsdatum+Uhrzeit
  const sortedAufgaben = useMemo(() => {
    const priorityOrder: Record<string, number> = { dringend: 0, hoch: 1, mittel: 2, niedrig: 3 };
    const myId = effectiveUserId;
    return [...filteredAufgaben].sort((a, b) => {
      // Erledigte IMMER ganz nach unten
      if (a.status === 'erledigt' && b.status !== 'erledigt') return 1;
      if (a.status !== 'erledigt' && b.status === 'erledigt') return -1;
      // Beobachter-Aufgaben nach unten, vor erledigte
      const aMine = a.zugewiesen_an === myId || (a.zugewiesen_an_liste || []).includes(myId);
      const bMine = b.zugewiesen_an === myId || (b.zugewiesen_an_liste || []).includes(myId);
      if (aMine && !bMine) return -1;
      if (!aMine && bMine) return 1;

      // Sortierung je nach gewähltem Modus
      switch (sortierung) {
        case 'faelligkeit-asc': {
          // Fälligkeitsdatum aufsteigend (früheste zuerst) — mit Uhrzeit
          if (a.faellig_am && b.faellig_am) {
            const cmp = new Date(a.faellig_am).getTime() - new Date(b.faellig_am).getTime();
            if (cmp !== 0) return cmp;
          }
          if (a.faellig_am && !b.faellig_am) return -1;
          if (!a.faellig_am && b.faellig_am) return 1;
          // Sekundär: Priorität
          const pa = priorityOrder[a.prioritaet] ?? 2;
          const pb = priorityOrder[b.prioritaet] ?? 2;
          return pa - pb;
        }
        case 'faelligkeit-desc': {
          // Fälligkeitsdatum absteigend (späteste zuerst)
          if (a.faellig_am && b.faellig_am) {
            const cmp = new Date(b.faellig_am).getTime() - new Date(a.faellig_am).getTime();
            if (cmp !== 0) return cmp;
          }
          if (a.faellig_am && !b.faellig_am) return -1;
          if (!a.faellig_am && b.faellig_am) return 1;
          return 0;
        }
        case 'prioritaet': {
          // Priorität zuerst, dann Fälligkeitsdatum
          const pa = priorityOrder[a.prioritaet] ?? 2;
          const pb = priorityOrder[b.prioritaet] ?? 2;
          if (pa !== pb) return pa - pb;
          if (a.faellig_am && b.faellig_am) return new Date(a.faellig_am).getTime() - new Date(b.faellig_am).getTime();
          if (a.faellig_am) return -1;
          if (b.faellig_am) return 1;
          return 0;
        }
        case 'erstellt': {
          // Neueste zuerst
          return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
        }
        default:
          return 0;
      }
    });
  }, [filteredAufgaben, effectiveUserId, sortierung]);

  // Zähler pro Liste (basierend auf sichtbaren Aufgaben)
  const listCounts = useMemo(() => {
    if (!sichtbareAufgaben) return {} as Record<ListId, number>;
    const today = new Date().toISOString().split('T')[0];
    const myId = effectiveUserId;
    const counts: Record<string, number> = {};

    counts['mein-tag'] = sichtbareAufgaben.filter(a => {
      const isToday = a.faellig_am && a.faellig_am.startsWith(today);
      const isMine = a.zugewiesen_an === myId || (a.zugewiesen_an_liste || []).includes(myId);
      return (isToday || isMine) && a.status !== 'erledigt';
    }).length;
    counts['wichtig'] = sichtbareAufgaben.filter(a => (a.prioritaet === 'hoch' || a.prioritaet === 'dringend') && a.status !== 'erledigt').length;
    counts['geplant'] = sichtbareAufgaben.filter(a => a.faellig_am && a.status !== 'erledigt').length;
    counts['zugewiesen'] = sichtbareAufgaben.filter(a => (a.zugewiesen_an === myId || (a.zugewiesen_an_liste || []).includes(myId)) && a.status !== 'erledigt').length;
    counts['wiederkehrend'] = sichtbareAufgaben.filter(a => a.wiederholung_typ && a.status !== 'erledigt').length;
    counts['alle'] = sichtbareAufgaben.length;
    // Dynamisch für ALLE Betriebe (inkl. adminOnly) Zähler berechnen
    BETRIEB_LISTS.forEach(list => {
      counts[list.id] = sichtbareAufgaben.filter(a => a.business_unit === list.id && a.status !== 'erledigt').length;
    });

    return counts as Record<ListId, number>;
  }, [sichtbareAufgaben, effectiveUserId, BETRIEB_LISTS]);

  // ===== NEUE AUFGABE ERSTELLEN =====
  // WICHTIG: effectiveUserId verwenden (nicht profile?.id), damit bei Impersonation
  // die Aufgabe dem impersonierten Mitarbeiter gehört und sofort sichtbar ist.
  const handleCreateTask = useCallback(async () => {
    if (!newTaskTitle.trim()) return;
    // Alle Betrieb-IDs für die Zuordnung bei Aufgaben-Erstellung
    const allBetriebIds = ALL_BETRIEB_LISTS.map(l => l.id);
    const businessUnit = allBetriebIds.includes(activeList as any)
      ? activeList
      : 'gutachten';

    // Immer den effektiven User als Ersteller und Zugewiesenen verwenden
    const userId = effectiveUserId;

    const result = await createAufgabe({
      titel: newTaskTitle.trim(),
      business_unit: businessUnit,
      erstellt_von: userId,
      // Bei "Mein Tag" und "Mir zugewiesen" automatisch dem User zuweisen
      zugewiesen_an: (activeList === 'zugewiesen' || activeList === 'mein-tag') ? userId : undefined,
      zugewiesen_an_liste: (activeList === 'zugewiesen' || activeList === 'mein-tag') ? [userId] : undefined,
      prioritaet: activeList === 'wichtig' ? 'hoch' : 'mittel',
      faellig_am: activeList === 'mein-tag' ? new Date().toISOString().split('T')[0] : undefined,
    });
    setNewTaskTitle('');
    setShowNewTask(false);

    // Detail-Panel sofort öffnen, damit der Anwender weitere Details eingeben kann
    if (result) {
      setSelectedAufgabe({
        ...result,
        mitarbeiter: null,
        mitarbeiter_liste: [],
        ersteller: null,
      } as AufgabeMitMitarbeiter);
    }
  }, [newTaskTitle, activeList, effectiveUserId, createAufgabe]);

  // ===== AKTIVE LISTE INFO =====
  const activeListDef = ALL_LISTS.find(l => l.id === activeList) || SMART_LISTS[0];

  // ===== LOADING / ERROR =====
  if (loading) {
    return (
      <div className="flex items-center justify-center h-full min-h-[400px]">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin text-[#B91C1C] mx-auto mb-3" />
          <p className="text-sm text-gray-500">Aufgaben werden geladen...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-full min-h-[400px]">
        <div className="text-center">
          <AlertCircle className="w-8 h-8 text-red-500 mx-auto mb-3" />
          <p className="text-sm text-red-600">{error}</p>
        </div>
      </div>
    );
  }

  // Mobile: Sidebar als Overlay, Detail als Full-Screen
  const isMobileView = typeof window !== 'undefined' && window.innerWidth < 768;

  return (
    <div className="flex h-[calc(100vh-56px)] md:h-[calc(100vh-56px)] overflow-hidden">
      {/* ===== SPALTE 1: Listen-Navigation ===== */}
      {/* Desktop: Sidebar links */}
      <aside className={`hidden md:flex flex-shrink-0 bg-gray-50 border-r border-gray-200 flex-col transition-all duration-200 ${sidebarCollapsed ? 'w-0 overflow-hidden' : 'w-[220px]'}`}>
        {/* Header */}
        <div className="px-4 py-4 border-b border-gray-200 bg-white">
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-lg bg-[#B91C1C]/10 flex items-center justify-center">
              <CheckSquare className="w-4 h-4 text-[#B91C1C]" />
            </div>
            <div>
              <h2 className="text-sm font-bold text-gray-900">Aufgaben</h2>
              <p className="text-xs text-gray-500">{aufgaben.length} gesamt</p>
            </div>
          </div>
        </div>

        {/* Listen */}
        <nav className="flex-1 overflow-y-auto py-2 px-2" style={{ scrollbarWidth: 'thin' }}>
          {/* Smart Lists */}
          <div className="px-2 pt-1 pb-2">
            <span className="text-[9px] font-semibold uppercase tracking-widest text-gray-400">
              Smart-Listen
            </span>
          </div>
          {SMART_LISTS.map((list) => (
            <ListNavItem
              key={list.id}
              list={list}
              isActive={activeList === list.id}
              count={listCounts[list.id] || 0}
              onClick={() => { setActiveList(list.id); setSelectedAufgabe(null); }}
            />
          ))}

          {/* Betrieb-Listen */}
          <div className="px-2 pt-4 pb-2">
            <span className="text-[9px] font-semibold uppercase tracking-widest text-gray-400">
              Betriebe
            </span>
          </div>
          {BETRIEB_LISTS.map((list) => (
            <ListNavItem
              key={list.id}
              list={list}
              isActive={activeList === list.id}
              count={listCounts[list.id] || 0}
              onClick={() => { setActiveList(list.id); setSelectedAufgabe(null); }}
            />
          ))}
        </nav>
      </aside>

      {/* Mobile: Sidebar als Overlay */}
      {!sidebarCollapsed && (
        <div className="md:hidden fixed inset-0 z-[60]">
          <div className="absolute inset-0 bg-black/40" onClick={() => setSidebarCollapsed(true)} />
          <aside className="absolute left-0 top-0 bottom-0 w-[280px] bg-gray-50 border-r border-gray-200 flex flex-col shadow-xl animate-in slide-in-from-left duration-200 overflow-y-auto">
            {/* Header */}
            <div className="px-4 py-4 border-b border-gray-200 bg-white">
              <div className="flex items-center gap-2.5">
                <div className="w-8 h-8 rounded-lg bg-[#B91C1C]/10 flex items-center justify-center">
                  <CheckSquare className="w-4 h-4 text-[#B91C1C]" />
                </div>
                <div>
                  <h2 className="text-sm font-bold text-gray-900">Aufgaben</h2>
                  <p className="text-xs text-gray-500">{aufgaben.length} gesamt</p>
                </div>
              </div>
            </div>
            <nav className="flex-1 overflow-y-auto py-2 px-2" style={{ scrollbarWidth: 'thin' }}>
              <div className="px-2 pt-1 pb-2">
                <span className="text-[9px] font-semibold uppercase tracking-widest text-gray-400">Smart-Listen</span>
              </div>
              {SMART_LISTS.map((list) => (
                <ListNavItem key={list.id} list={list} isActive={activeList === list.id} count={listCounts[list.id] || 0}
                  onClick={() => { setActiveList(list.id); setSelectedAufgabe(null); setSidebarCollapsed(true); }} />
              ))}
              <div className="px-2 pt-4 pb-2">
                <span className="text-[9px] font-semibold uppercase tracking-widest text-gray-400">Betriebe</span>
              </div>
              {BETRIEB_LISTS.map((list) => (
                <ListNavItem key={list.id} list={list} isActive={activeList === list.id} count={listCounts[list.id] || 0}
                  onClick={() => { setActiveList(list.id); setSelectedAufgabe(null); setSidebarCollapsed(true); }} />
              ))}
            </nav>
          </aside>
        </div>
      )}

      {/* ===== SPALTE 2: Aufgabenliste ===== */}
      <div className="flex-1 flex flex-col min-w-0 bg-white">
        {/* Fristen-Zeitstrahl (read-only, Baustein 1) */}
        <div className="flex-shrink-0 max-h-56 overflow-y-auto border-b border-gray-200 bg-gray-50/60">
          <Zeitstrahl />
        </div>
        {/* Liste Header */}
        <div className="px-5 py-4 border-b border-gray-200 bg-white">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              {/* Mobile: Sidebar Toggle */}
              <button
                onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
                className="md:hidden w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100"
              >
                <ListTodo className="w-4 h-4 text-gray-500" />
              </button>
              <activeListDef.icon className="w-5 h-5" style={{ color: activeListDef.color }} />
              <div>
                <h1 className="text-lg font-bold text-gray-900">{activeListDef.label}</h1>
                <p className="text-xs text-gray-500">
                  {sortedAufgaben.filter(a => a.status !== 'erledigt').length} offen
                  {sortedAufgaben.filter(a => a.status === 'erledigt').length > 0 && (
                    <span className="ml-1">· {sortedAufgaben.filter(a => a.status === 'erledigt').length} erledigt</span>
                  )}
                </p>
              </div>
            </div>
          </div>

          {/* Filterleiste */}
          <div className="flex flex-wrap items-center gap-1.5 sm:gap-2 mt-3 pb-2">
            {/* Suche */}
            <div className="relative flex-1 min-w-[140px] max-w-[240px]">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Suchen..."
                className="w-full pl-8 pr-7 py-1.5 text-xs rounded-lg border border-gray-200 bg-gray-50 focus:outline-none focus:ring-2 focus:ring-[#B91C1C]/20 focus:border-[#B91C1C]"
              />
              {searchQuery && (
                <button
                  type="button"
                  onClick={() => setSearchQuery('')}
                  aria-label="Suche zurücksetzen"
                  className="absolute right-1.5 top-1/2 -translate-y-1/2 p-0.5 rounded hover:bg-gray-200 text-gray-400 hover:text-gray-600"
                >
                  <X className="w-3 h-3" />
                </button>
              )}
            </div>

            {/* Priorität Filter */}
            <select
              value={filterPrioritaet}
              onChange={(e) => setFilterPrioritaet(e.target.value)}
              className="text-xs px-2.5 py-1.5 rounded-lg border border-gray-200 bg-gray-50 focus:outline-none focus:ring-2 focus:ring-[#B91C1C]/20 text-gray-600"
            >
              <option value="">Alle Prioritäten</option>
              <option value="dringend">Dringend</option>
              <option value="hoch">Hoch</option>
              <option value="mittel">Mittel</option>
              <option value="niedrig">Niedrig</option>
            </select>

            {/* Status Filter */}
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="text-xs px-2.5 py-1.5 rounded-lg border border-gray-200 bg-gray-50 focus:outline-none focus:ring-2 focus:ring-[#B91C1C]/20 text-gray-600"
            >
              <option value="">Alle Status</option>
              <option value="offen">Offen</option>
              <option value="in_bearbeitung">In Bearbeitung</option>
              <option value="erledigt">Erledigt</option>
            </select>

            {/* Mitarbeiter Filter */}
            <select
              value={filterMitarbeiter}
              onChange={(e) => setFilterMitarbeiter(e.target.value)}
              className="text-xs px-2.5 py-1.5 rounded-lg border border-gray-200 bg-gray-50 focus:outline-none focus:ring-2 focus:ring-[#B91C1C]/20 text-gray-600"
            >
              <option value="">Alle Mitarbeiter</option>
              {(mitarbeiterList || []).filter((m: any) => m.ist_aktiv && m.rolle !== 'ki_agent' && m.id !== KI_AGENT_ID).map((m: any) => (
                <option key={m.id} value={m.id}>{m.vorname} {m.nachname}</option>
              ))}
            </select>

            {/* Sortierung */}
            <div className="relative">
              <select
                value={sortierung}
                onChange={(e) => setSortierung(e.target.value as any)}
                className="text-xs px-2.5 py-1.5 rounded-lg border border-gray-200 bg-gray-50 focus:outline-none focus:ring-2 focus:ring-[#B91C1C]/20 text-gray-600 appearance-none pr-7"
              >
                <option value="faelligkeit-asc">↑ Fälligkeit (früheste)</option>
                <option value="faelligkeit-desc">↓ Fälligkeit (späteste)</option>
                <option value="prioritaet">Priorität</option>
                <option value="erstellt">Erstellt (neueste)</option>
              </select>
              <ArrowUpDown className="absolute right-2 top-1/2 -translate-y-1/2 w-3 h-3 text-gray-400 pointer-events-none" />
            </div>

            {/* Filter zurücksetzen */}
            {(filterPrioritaet || filterStatus || filterMitarbeiter || searchQuery) && (
              <button
                onClick={() => { setFilterPrioritaet(''); setFilterStatus(''); setFilterMitarbeiter(''); setSearchQuery(''); }}
                className="text-xs text-[#B91C1C] hover:text-[#991B1B] font-medium px-2 py-1.5 rounded-lg hover:bg-red-50 transition-colors flex items-center gap-1"
              >
                <X className="w-3 h-3" />
                Filter zurücksetzen
              </button>
            )}
          </div>
        </div>

        {/* Aufgabenliste */}
        <div className="flex-1 overflow-y-auto">
          {/* Neue Aufgabe Input */}
          <div className="px-5 py-3 border-b border-gray-100">
            {showNewTask ? (
              <div className="flex items-center gap-2">
                <Circle className="w-5 h-5 text-gray-300 flex-shrink-0" />
                <input
                  type="text"
                  value={newTaskTitle}
                  onChange={(e) => setNewTaskTitle(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') handleCreateTask();
                    if (e.key === 'Escape') { setShowNewTask(false); setNewTaskTitle(''); }
                  }}
                  placeholder="Aufgabe eingeben..."
                  className="flex-1 text-sm border-none outline-none bg-transparent placeholder:text-gray-400"
                  autoFocus
                />
                <button
                  onClick={handleCreateTask}
                  disabled={!newTaskTitle.trim()}
                  className="px-3 py-1.5 text-xs font-semibold text-white bg-[#B91C1C] rounded-lg hover:bg-[#991B1B] disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                >
                  Erstellen
                </button>
                <button
                  onClick={() => { setShowNewTask(false); setNewTaskTitle(''); }}
                  className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-400"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            ) : (
              <button
                onClick={() => setShowNewTask(true)}
                className="flex items-center gap-2 text-sm text-[#B91C1C] hover:text-[#991B1B] font-medium transition-colors w-full"
              >
                <Plus className="w-5 h-5" />
                <span>Aufgabe hinzufügen</span>
              </button>
            )}
          </div>

          {/* Aufgaben */}
          {sortedAufgaben.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 text-gray-400">
              <CheckSquare className="w-12 h-12 mb-3 text-gray-200" />
              <p className="text-sm font-medium">Keine Aufgaben</p>
              <p className="text-xs mt-1">Erstelle eine neue Aufgabe mit dem + Button</p>
            </div>
          ) : (
            <div className="divide-y divide-gray-50">
              {sortedAufgaben.map((aufgabe) => (
                <TaskRow
                  key={aufgabe.id}
                  aufgabe={aufgabe}
                  isSelected={currentAufgabe?.id === aufgabe.id}
                  onToggle={() => toggleStatus(aufgabe.id, aufgabe.status)}
                  onSelect={() => setSelectedAufgabe(aufgabe)}
                  myId={effectiveUserId}
                  effectiveIsAdmin={effectiveIsAdmin}
                  isPending={pendingStatusIds.has(aufgabe.id)}
                />
              ))}
            </div>
          )}
        </div>
      </div>

      {/* ===== SPALTE 3: Detail-Panel ===== */}
      {/* Desktop: Side Panel */}
      <div className="hidden md:block">
        {currentAufgabe && (
          <TaskDetailPanel
            aufgabe={currentAufgabe}
            mitarbeiterList={mitarbeiterList || []}
            myProfileId={effectiveUserId}
            effectiveIsAdmin={effectiveIsAdmin}
            onUpdate={updateAufgabe}
            isPending={pendingStatusIds.has(currentAufgabe.id)}
            onAssignToMe={assignToMe}
            onUnassign={unassign}
            onRemoveAssignee={removeAssignee}
            onUpdateCheckliste={updateCheckliste}
            onDelete={deleteAufgabe}
            onClose={() => setSelectedAufgabe(null)}
          />
        )}
      </div>

      {/* Mobile: Full-Screen Overlay */}
      {currentAufgabe && (
        <div className="md:hidden fixed inset-0 z-[60] bg-white">
          <div className="h-full overflow-y-auto pb-24">
            <TaskDetailPanel
              aufgabe={currentAufgabe}
              mitarbeiterList={mitarbeiterList || []}
              myProfileId={effectiveUserId}
              effectiveIsAdmin={effectiveIsAdmin}
              onUpdate={updateAufgabe}
              isPending={pendingStatusIds.has(currentAufgabe.id)}
              onAssignToMe={assignToMe}
              onUnassign={unassign}
              onRemoveAssignee={removeAssignee}
              onUpdateCheckliste={updateCheckliste}
              onDelete={deleteAufgabe}
              onClose={() => setSelectedAufgabe(null)}
            />
          </div>
        </div>
      )}
    </div>
  );
}

// ============================================================
// LISTEN-NAV-ITEM
// ============================================================
function ListNavItem({ list, isActive, count, onClick }: {
  list: ListDef;
  isActive: boolean;
  count: number;
  onClick: () => void;
}) {
  const Icon = list.icon;
  return (
    <button
      onClick={onClick}
      className={`w-full flex items-center gap-2.5 rounded-lg mb-0.5 px-2.5 py-2 transition-all duration-150 ${
        isActive
          ? 'bg-white shadow-sm border border-gray-200 text-gray-900'
          : 'text-gray-600 hover:text-gray-900 hover:bg-white/60'
      }`}
    >
      <Icon className="w-4 h-4 flex-shrink-0" style={{ color: isActive ? list.color : undefined }} />
      <span className="text-[13px] font-medium truncate flex-1 text-left">{list.label}</span>
      {count > 0 && (
        <span className={`text-xs font-bold px-1.5 py-0.5 rounded-full ${
          isActive ? 'bg-gray-100 text-gray-700' : 'bg-gray-100 text-gray-500'
        }`}>
          {count}
        </span>
      )}
    </button>
  );
}

// ============================================================
// AUFGABEN-ZEILE
// ============================================================
function TaskRow({ aufgabe, isSelected, onToggle, onSelect, myId, effectiveIsAdmin, isPending = false }: {
  aufgabe: AufgabeMitMitarbeiter;
  isSelected: boolean;
  onToggle: () => void;
  onSelect: () => void;
  myId: string;
  effectiveIsAdmin: boolean;
  isPending?: boolean;
}) {
  const isErledigt = aufgabe.status === 'erledigt';
  const prio = PRIORITAET_CONFIG[aufgabe.prioritaet] || PRIORITAET_CONFIG.mittel;
  const betrieb = BETRIEBE[aufgabe.business_unit as BetriebId];
  const isOverdue = aufgabe.faellig_am && !isErledigt && new Date(aufgabe.faellig_am) < new Date(new Date().toISOString().split('T')[0]);
  const isAssignedToMe = aufgabe.zugewiesen_an === myId || (aufgabe.zugewiesen_an_liste || []).includes(myId);
  const isObserver = !isAssignedToMe && !isErledigt; // Beobachter: erstellt aber nicht zugewiesen

  // Checklisten-Fortschritt
  const checklistTotal = aufgabe.checkliste?.length || 0;
  const checklistDone = aufgabe.checkliste?.filter(c => c.done).length || 0;

  return (
    <div
      className={`flex items-start gap-3 px-3 sm:px-5 py-3 cursor-pointer transition-colors group ${
        isSelected ? 'bg-blue-50/60 border-r-2 border-[#B91C1C]' : 'hover:bg-gray-50'
      } ${isErledigt ? 'opacity-50' : ''} ${isObserver ? 'opacity-50' : ''}`}
      onClick={onSelect}
    >
      {/* Checkbox */}
      <button
        onClick={(e) => { e.stopPropagation(); onToggle(); }}
        disabled={isPending}
        className="mt-0.5 flex-shrink-0 group/check"
      >
        {isPending ? (
          <Loader2 className="w-5 h-5 text-gray-400 animate-spin" />
        ) : isErledigt ? (
          <CheckCircle2 className="w-5 h-5 text-green-500" />
        ) : (
          <Circle className="w-5 h-5 text-gray-300 group-hover/check:text-[#B91C1C] transition-colors" />
        )}
      </button>

      {/* Content */}
      <div className="flex-1 min-w-0">
        <p className={`text-sm font-medium ${isErledigt ? 'line-through text-gray-400' : 'text-gray-900'}`}>
          {aufgabe.titel}
        </p>
        <div className="flex items-center gap-2 mt-1 flex-wrap">
          {/* Betrieb */}
          <span className="text-xs font-medium px-1.5 py-0.5 rounded bg-gray-100 text-gray-500">
            {betrieb?.name || aufgabe.business_unit}
          </span>
          {/* Priorität */}
          {aufgabe.prioritaet !== 'mittel' && (
            <span className={`text-xs font-medium px-1.5 py-0.5 rounded border ${prio.bg} ${prio.color}`}>
              {prio.label}
            </span>
          )}
          {/* Fälligkeitsdatum mit Uhrzeit */}
          {aufgabe.faellig_am && (
            <span className={`text-xs flex items-center gap-1 ${isOverdue ? 'text-red-500 font-semibold' : 'text-gray-400'}`}>
              <Clock className="w-3 h-3" />
              {(() => {
                const d = new Date(aufgabe.faellig_am);
                const hatUhrzeit = d.getHours() !== 0 || d.getMinutes() !== 0;
                const datumStr = d.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit' });
                if (hatUhrzeit) {
                  return `${datumStr} ${d.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' })}`;
                }
                return datumStr;
              })()}
            </span>
          )}
          {/* Wiederkehrend-Badge */}
          {aufgabe.wiederholung_typ && (
            <span className="text-[9px] font-medium px-1.5 py-0.5 rounded bg-purple-50 text-purple-600 border border-purple-200 flex items-center gap-0.5">
              <Repeat className="w-2.5 h-2.5" />
              {WIEDERHOLUNG_LABELS[aufgabe.wiederholung_typ as keyof typeof WIEDERHOLUNG_LABELS] || aufgabe.wiederholung_typ}
            </span>
          )}
          {/* Checkliste */}
          {checklistTotal > 0 && (
            <span className="text-xs text-gray-400 flex items-center gap-1">
              <ListChecks className="w-3 h-3" />
              {checklistDone}/{checklistTotal}
            </span>
          )}
          {/* KI-Agent Badge entfernt — keine KI-Erwähnungen für Mitarbeiter */}
          {/* E-Mail-Aufgabe Badge */}
          {aufgabe.kategorie === 'email_aufgabe' && (() => {
            const emailMeta = parseEmailMetadata(aufgabe.wiedervorlage_notiz || null);
            return (
              <>
                <span className="text-[9px] font-medium px-1.5 py-0.5 rounded bg-blue-50 text-blue-600 border border-blue-200 flex items-center gap-0.5">
                  <Mail className="w-2.5 h-2.5" />
                  E-Mail
                </span>
                {emailMeta?.antwortErwartet && (
                  <span className="text-[9px] font-medium px-1.5 py-0.5 rounded bg-amber-50 text-amber-700 border border-amber-300 flex items-center gap-0.5 animate-pulse">
                    <Send className="w-2.5 h-2.5" />
                    Antwort erwartet
                  </span>
                )}
                {emailMeta?.anhaenge && emailMeta.anhaenge.length > 0 && (
                  <span className="text-[9px] font-medium px-1.5 py-0.5 rounded bg-gray-100 text-gray-500 flex items-center gap-0.5">
                    <Paperclip className="w-2.5 h-2.5" />
                    {emailMeta.anhaenge.length}
                  </span>
                )}
              </>
            );
          })()}
          {/* Beobachter-Badge */}
          {isObserver && (
            <span className="text-[9px] font-medium px-1.5 py-0.5 rounded bg-amber-50 text-amber-600 border border-amber-200">
              Beobachter
            </span>
          )}
        </div>
      </div>

      {/* Zugewiesen Avatars (Multi-Assignee) */}
      {(aufgabe.mitarbeiter_liste?.length > 0 || aufgabe.mitarbeiter) && (
        <div className="flex -space-x-1.5 flex-shrink-0 mt-0.5">
          {(aufgabe.mitarbeiter_liste?.length > 0 ? aufgabe.mitarbeiter_liste : (aufgabe.mitarbeiter ? [aufgabe.mitarbeiter] : []))
            .filter(m => m.id !== KI_AGENT_ID)
            .slice(0, 3).map((m, i) => (
            <div
              key={m.id || i}
              className="w-6 h-6 rounded-full flex items-center justify-center text-[9px] font-bold text-white border-2 border-white"
              style={{ backgroundColor: ['#6B7280', '#2563EB', '#059669', '#D97706', '#DC2626'][i % 5] }}
              title={`${m.vorname} ${m.nachname}`}
            >
              {`${m.vorname?.[0]}${m.nachname?.[0]}`}
            </div>
          ))}
          {(aufgabe.mitarbeiter_liste?.length || 0) > 3 && (
            <div className="w-6 h-6 rounded-full flex items-center justify-center text-[9px] font-bold text-gray-600 bg-gray-200 border-2 border-white">
              +{(aufgabe.mitarbeiter_liste?.length || 0) - 3}
            </div>
          )}
        </div>
      )}

      {/* Chevron */}
      <ChevronRight className="w-4 h-4 text-gray-300 flex-shrink-0 mt-1 opacity-0 group-hover:opacity-100 transition-opacity" />
    </div>
  );
}

// ============================================================
// DETAIL-PANEL
// ============================================================
function TaskDetailPanel({ aufgabe, mitarbeiterList, myProfileId, effectiveIsAdmin, onUpdate, isPending = false, onAssignToMe, onUnassign, onRemoveAssignee, onUpdateCheckliste, onDelete, onClose }: {
  aufgabe: AufgabeMitMitarbeiter;
  mitarbeiterList: any[];
  myProfileId: string;
  effectiveIsAdmin: boolean;
  onUpdate: (id: string, data: any) => Promise<boolean>;
  isPending?: boolean;
  onAssignToMe: (id: string, mitarbeiterId: string) => Promise<boolean>;
  onUnassign: (id: string) => Promise<boolean>;
  onRemoveAssignee: (id: string, mitarbeiterId: string) => Promise<boolean>;
  onUpdateCheckliste: (id: string, checkliste: ChecklistItem[]) => Promise<boolean>;
  onDelete: (id: string) => Promise<boolean>;
  onClose: () => void;
}) {
  // ===== NEUE FEATURES: Dateien, Kommentare, Labels, Unteraufgaben =====
  const { dateien, uploadDatei, deleteDatei } = useAufgabenDateien(aufgabe.id);
  const { kommentare, addKommentar, deleteKommentar } = useAufgabenKommentare(aufgabe.id);
  const { labels } = useAufgabenLabels();
  const { zugeordneteLabels, toggleLabel } = useAufgabeLabelZuordnung(aufgabe.id);
  const { unteraufgaben, addUnteraufgabe, toggleUnteraufgabe, deleteUnteraufgabe } = useUnteraufgaben(aufgabe.id);
  const [newKommentar, setNewKommentar] = useState('');
  const [newUnteraufgabe, setNewUnteraufgabe] = useState('');
  const [showLabelPicker, setShowLabelPicker] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [editingTitle, setEditingTitle] = useState(false);
  const [titleValue, setTitleValue] = useState(aufgabe.titel);
  const [beschreibung, setBeschreibung] = useState(aufgabe.beschreibung || '');
  const [editingBeschreibung, setEditingBeschreibung] = useState(false);
  const [newCheckItem, setNewCheckItem] = useState('');
  const [showAssignDropdown, setShowAssignDropdown] = useState(false);
  const assignDropdownRef = useRef<HTMLDivElement>(null);

  // Click-Outside-Handler für Zuweisungs-Dropdown
  useEffect(() => {
    if (!showAssignDropdown) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (assignDropdownRef.current && !assignDropdownRef.current.contains(e.target as Node)) {
        setShowAssignDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showAssignDropdown]);

  // Sync state wenn Aufgabe wechselt
  const aufgabeId = aufgabe.id;
  useMemo(() => {
    setTitleValue(aufgabe.titel);
    setBeschreibung(aufgabe.beschreibung || '');
    setEditingTitle(false);
    setEditingBeschreibung(false);
    setNewCheckItem('');
    setShowAssignDropdown(false);
  }, [aufgabeId]);

  const isErledigt = aufgabe.status === 'erledigt';
  const prio = PRIORITAET_CONFIG[aufgabe.prioritaet] || PRIORITAET_CONFIG.mittel;
  const betrieb = BETRIEBE[aufgabe.business_unit as BetriebId];
  const isMine = aufgabe.zugewiesen_an === myProfileId;

  // Checkliste
  const checkliste = aufgabe.checkliste || [];

  const handleAddCheckItem = async () => {
    if (!newCheckItem.trim()) return;
    const newItem: ChecklistItem = {
      id: crypto.randomUUID(),
      text: newCheckItem.trim(),
      done: false,
    };
    await onUpdateCheckliste(aufgabe.id, [...checkliste, newItem]);
    setNewCheckItem('');
  };

  const handleToggleCheckItem = async (itemId: string) => {
    const updated = checkliste.map(c => c.id === itemId ? { ...c, done: !c.done } : c);
    await onUpdateCheckliste(aufgabe.id, updated);
  };

  const handleDeleteCheckItem = async (itemId: string) => {
    const updated = checkliste.filter(c => c.id !== itemId);
    await onUpdateCheckliste(aufgabe.id, updated);
  };

  const handleSaveTitle = async () => {
    if (titleValue.trim() && titleValue !== aufgabe.titel) {
      await onUpdate(aufgabe.id, { titel: titleValue.trim() });
    }
    setEditingTitle(false);
  };

  const handleSaveBeschreibung = async () => {
    if (beschreibung !== (aufgabe.beschreibung || '')) {
      await onUpdate(aufgabe.id, { beschreibung: beschreibung || null });
    }
    setEditingBeschreibung(false);
  };

  const handleDelete = async () => {
    if (window.confirm('Aufgabe wirklich löschen?')) {
      await onDelete(aufgabe.id);
      onClose();
    }
  };

  // Wiederkehrend-Info Text
  const wiederholungInfo = aufgabe.wiederholung_typ ? (
    <div className="mt-1 text-xs text-purple-500 flex items-center gap-1">
      <Repeat className="w-3 h-3" />
      {WIEDERHOLUNG_LABELS[aufgabe.wiederholung_typ as keyof typeof WIEDERHOLUNG_LABELS]}
      {aufgabe.wiederholung_ende && (
        <span className="text-gray-400"> bis {new Date(aufgabe.wiederholung_ende).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' })}</span>
      )}
    </div>
  ) : null;

  return (
    <aside className="w-[480px] flex-shrink-0 bg-white border-l border-gray-200 flex flex-col h-full overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 bg-gray-50">
        <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Details</span>
        <button onClick={onClose} className="p-1 rounded-lg hover:bg-gray-200 text-gray-400">
          <X className="w-4 h-4" />
        </button>
      </div>

      {/* Scrollbarer Inhalt */}
      <div className="flex-1 overflow-y-auto px-4 py-4 space-y-5" style={{ scrollbarWidth: 'thin' }}>
        {/* Status + Titel */}
        <div>
          <div className="flex items-start gap-3">
            <button
              onClick={() => onUpdate(aufgabe.id, { status: isErledigt ? 'offen' : 'erledigt' })}
              disabled={isPending}
              className="mt-1 flex-shrink-0"
            >
              {isPending ? (
                <Loader2 className="w-6 h-6 text-gray-400 animate-spin" />
              ) : isErledigt ? (
                <CheckCircle2 className="w-6 h-6 text-green-500" />
              ) : (
                <Circle className="w-6 h-6 text-gray-300 hover:text-[#B91C1C] transition-colors" />
              )}
            </button>
            <div className="flex-1">
              {editingTitle ? (
                <input
                  type="text"
                  value={titleValue}
                  onChange={(e) => setTitleValue(e.target.value)}
                  onBlur={handleSaveTitle}
                  onKeyDown={(e) => { if (e.key === 'Enter') handleSaveTitle(); if (e.key === 'Escape') setEditingTitle(false); }}
                  className="text-base font-bold text-gray-900 w-full border-b-2 border-[#B91C1C] outline-none bg-transparent pb-1"
                  autoFocus
                />
              ) : (
                <h3
                  className={`text-base font-bold cursor-pointer hover:text-[#B91C1C] transition-colors ${isErledigt ? 'line-through text-gray-400' : 'text-gray-900'}`}
                  onClick={() => setEditingTitle(true)}
                >
                  {aufgabe.titel}
                </h3>
              )}
            </div>
          </div>
        </div>

        {/* E-Mail-Referenz (wenn aus E-Mail erstellt) */}
        {aufgabe.kategorie === 'email_aufgabe' && (() => {
          const emailMeta = parseEmailMetadata(aufgabe.wiedervorlage_notiz || null);
          if (!emailMeta) return null;
          return (
            <div className="bg-blue-50/50 border border-blue-200 rounded-xl p-3 space-y-2">
              <div className="flex items-center gap-2">
                <Mail className="w-4 h-4 text-blue-600" />
                <span className="text-xs font-bold text-blue-800">Aus E-Mail erstellt</span>
                {emailMeta.antwortErwartet && (
                  <span className="text-[9px] font-medium px-1.5 py-0.5 rounded bg-amber-100 text-amber-700 border border-amber-300 animate-pulse">
                    Antwort erwartet
                  </span>
                )}
              </div>
              <div className="text-xs text-blue-700">
                <p className="font-medium">{emailMeta.subject}</p>
                <p className="text-blue-500">Von: {emailMeta.fromName || emailMeta.from}</p>
                <p className="text-blue-400 text-xs">{new Date(emailMeta.date).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</p>
              </div>
              {emailMeta.anhaenge && emailMeta.anhaenge.length > 0 && (
                <div className="flex items-center gap-1 text-xs text-blue-500">
                  <Paperclip className="w-3 h-3" />
                  {emailMeta.anhaenge.length} Anhang/Anhänge aus E-Mail
                </div>
              )}
              <button
                onClick={() => {
                  // Deep-Link: Navigiere zur Kommunikationsseite mit der E-Mail
                  window.location.href = `/kommunikation?email=${emailMeta.messageId}&thread=${emailMeta.threadId}`;
                }}
                className="flex items-center gap-1.5 text-xs font-semibold text-blue-700 hover:text-blue-900 bg-blue-100 hover:bg-blue-200 px-3 py-2 rounded-lg transition-colors w-full justify-center"
              >
                <ExternalLink className="w-3.5 h-3.5" />
                E-Mail öffnen & antworten
              </button>
              {emailMeta.antwortErwartet && (
                <button
                  onClick={async () => {
                    // Antwort-Status zurücksetzen
                    const updatedMeta = { ...emailMeta, antwortErwartet: false };
                    await onUpdate(aufgabe.id, { wiedervorlage_notiz: JSON.stringify(updatedMeta) });
                    toast.success('Antwort-Status aktualisiert');
                  }}
                  className="flex items-center gap-1.5 text-xs font-medium text-green-700 hover:text-green-900 bg-green-50 hover:bg-green-100 px-3 py-1.5 rounded-lg transition-colors w-full justify-center border border-green-200"
                >
                  <CheckCircle2 className="w-3 h-3" />
                  Antwort gesendet — als erledigt markieren
                </button>
              )}
            </div>
          );
        })()}

        {/* Metadaten */}
        <div className="space-y-3">
          {/* Zugewiesen an (Multi-Assignee) */}
          <div className="relative" ref={assignDropdownRef}>
            <label className="text-xs font-semibold uppercase tracking-wider text-gray-400 flex items-center gap-1.5 mb-1.5">
              <Users className="w-3 h-3" /> Zugewiesen an
              {(aufgabe.mitarbeiter_liste?.length || 0) > 0 && (
                <span className="text-gray-500 normal-case tracking-normal">({aufgabe.mitarbeiter_liste?.length || 0})</span>
              )}
            </label>

            {/* Zugewiesene Mitarbeiter-Liste */}
            {(aufgabe.mitarbeiter_liste?.length > 0) ? (
              <div className="space-y-1.5 mb-2">
                {aufgabe.mitarbeiter_liste
                  .filter(m => m.id !== KI_AGENT_ID)
                  .map((m, i) => (
                  <div key={m.id} className="flex items-center gap-2 group/assignee">
                    <div
                      className="w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold text-white flex-shrink-0"
                      style={{ backgroundColor: ['#6B7280', '#2563EB', '#059669', '#D97706', '#DC2626'][i % 5] }}
                    >
                      {`${m.vorname?.[0]}${m.nachname?.[0]}`}
                    </div>
                    <span className="text-sm text-gray-900 font-medium flex-1">{`${m.vorname} ${m.nachname}`}</span>
                    <button
                      onClick={() => onRemoveAssignee(aufgabe.id, m.id)}
                      className="p-1 rounded hover:bg-gray-100 text-gray-300 opacity-0 group-hover/assignee:opacity-100 transition-opacity"
                      title="Entfernen"
                    >
                      <X className="w-3.5 h-3.5" />
                    </button>
                  </div>
                ))}
              </div>
            ) : aufgabe.mitarbeiter ? (
              <div className="flex items-center gap-2 mb-2">
                <div className="w-7 h-7 rounded-full bg-gray-500 flex items-center justify-center text-xs font-bold text-white">
                  {aufgabe.mitarbeiter.vorname?.[0]}{aufgabe.mitarbeiter.nachname?.[0]}
                </div>
                <span className="text-sm text-gray-900 font-medium">{aufgabe.mitarbeiter.vorname} {aufgabe.mitarbeiter.nachname}</span>
                <button
                  onClick={() => onUnassign(aufgabe.id)}
                  className="ml-auto p-1 rounded hover:bg-gray-100 text-gray-400"
                  title="Zuweisung entfernen"
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              </div>
            ) : null}

            {/* Aktionen */}
            <div className="flex items-center gap-2">
              {!(aufgabe.zugewiesen_an_liste || []).includes(myProfileId) && (
                <button
                  onClick={() => onAssignToMe(aufgabe.id, myProfileId)}
                  className="text-xs font-medium text-[#B91C1C] hover:text-[#991B1B] px-2 py-1 rounded-lg hover:bg-red-50 transition-colors flex items-center gap-1"
                >
                  <UserPlus className="w-3 h-3" />
                  Mir zuweisen
                </button>
              )}
              <button
                onClick={() => setShowAssignDropdown(!showAssignDropdown)}
                className="text-xs font-medium text-gray-500 hover:text-gray-700 px-2 py-1 rounded-lg hover:bg-gray-100 transition-colors flex items-center gap-1"
              >
                <Plus className="w-3 h-3" />
                Mitarbeiter hinzufügen
              </button>
              {(aufgabe.mitarbeiter_liste?.length > 0 || aufgabe.mitarbeiter) && (
                <button
                  onClick={() => onUnassign(aufgabe.id)}
                  className="text-xs font-medium text-gray-400 hover:text-red-500 px-2 py-1 rounded-lg hover:bg-gray-100 transition-colors"
                  title="Alle entfernen"
                >
                  <Trash2 className="w-3 h-3" />
                </button>
              )}
            </div>

            {/* Dropdown */}
            {showAssignDropdown && (
              <div className="absolute z-20 mt-1 left-4 right-4 sm:left-0 sm:right-0 bg-white border border-gray-200 rounded-lg shadow-lg max-h-48 overflow-y-auto w-[calc(100vw-2rem)] sm:w-full">
                {mitarbeiterList.filter((m: any) => m.ist_aktiv && m.rolle !== 'ki_agent' && m.id !== KI_AGENT_ID).map((m: any) => {
                  const isAssigned = (aufgabe.zugewiesen_an_liste || []).includes(m.id);
                  return (
                    <button
                      key={m.id}
                      onClick={async () => {
                        if (isAssigned) {
                          await onRemoveAssignee(aufgabe.id, m.id);
                        } else {
                          await onAssignToMe(aufgabe.id, m.id);
                        }
                      }}
                      className={`w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-gray-50 text-left ${isAssigned ? 'bg-blue-50' : ''}`}
                    >
                      <div className={`w-6 h-6 rounded-full flex items-center justify-center text-[9px] font-bold text-white ${isAssigned ? 'bg-[#B91C1C]' : 'bg-gray-400'}`}>
                        {m.vorname?.[0]}{m.nachname?.[0]}
                      </div>
                      <span className="flex-1">{`${m.vorname} ${m.nachname}`}</span>
                      {isAssigned && <CheckCircle2 className="w-4 h-4 text-[#B91C1C]" />}
                    </button>
                  );
                })}
                <div className="border-t border-gray-100 px-3 py-2">
                  <button
                    onClick={() => setShowAssignDropdown(false)}
                    className="text-xs text-gray-500 hover:text-gray-700 font-medium"
                  >
                    Fertig
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Priorität */}
          <div>
            <label className="text-xs font-semibold uppercase tracking-wider text-gray-400 flex items-center gap-1.5 mb-1.5">
              <Flag className="w-3 h-3" /> Priorität
            </label>
            <div className="flex gap-1.5">
              {Object.entries(PRIORITAET_CONFIG).map(([key, cfg]) => (
                <button
                  key={key}
                  onClick={() => onUpdate(aufgabe.id, { prioritaet: key })}
                  className={`text-xs font-medium px-2 py-1 rounded-lg border transition-colors ${
                    aufgabe.prioritaet === key
                      ? `${cfg.bg} ${cfg.color} border-current`
                      : 'bg-gray-50 text-gray-400 border-gray-200 hover:bg-gray-100'
                  }`}
                >
                  {cfg.label}
                </button>
              ))}
            </div>
          </div>

          {/* Fälligkeitsdatum + Uhrzeit */}
          <div>
            <label className="text-xs font-semibold uppercase tracking-wider text-gray-400 flex items-center gap-1.5 mb-1.5">
              <CalendarIcon className="w-3 h-3" /> Fällig am
            </label>
            <DateTimePicker
              value={aufgabe.faellig_am}
              onChange={(iso) => onUpdate(aufgabe.id, { faellig_am: iso })}
            />
          </div>

          {/* Wiederkehrende Aufgabe */}
          <WiederholungSection
            aufgabe={aufgabe}
            onUpdate={onUpdate}
          />

          {/* Betrieb */}
          <div>
            <label className="text-xs font-semibold uppercase tracking-wider text-gray-400 flex items-center gap-1.5 mb-1.5">
              <Building2 className="w-3 h-3" /> Betrieb
            </label>
            <select
              value={aufgabe.business_unit}
              onChange={(e) => onUpdate(aufgabe.id, { business_unit: e.target.value })}
              className="w-full text-sm px-3 py-2 rounded-lg border border-gray-200 bg-gray-50 focus:outline-none focus:ring-2 focus:ring-[#B91C1C]/20"
            >
              <option value="gutachten">Kfz Gutachten</option>
              <option value="autoglas">Autoglas</option>
              <option value="mietwagen">Mietwagen</option>
              <option value="werkstatt">Werkstatt</option>
              {effectiveIsAdmin && (
                <>
                  <option value="protex">Protex / Amazon</option>
                  <option value="unfallgiganten">Unfallgiganten</option>
                  <option value="unfallwiki">Unfallwiki</option>
                  <option value="airbnb">Airbnb / Immobilien</option>
                  <option value="recht">Recht & Forderungen</option>
                  <option value="app_programmierung">APP Programmierung</option>
                </>
              )}
            </select>
          </div>

          {/* Status */}
          <div>
            <label className="text-xs font-semibold uppercase tracking-wider text-gray-400 flex items-center gap-1.5 mb-1.5">
              <SquarePen className="w-3 h-3" /> Status
            </label>
            <select
              value={aufgabe.status}
              onChange={(e) => onUpdate(aufgabe.id, { status: e.target.value })}
              className="w-full text-sm px-3 py-2 rounded-lg border border-gray-200 bg-gray-50 focus:outline-none focus:ring-2 focus:ring-[#B91C1C]/20"
            >
              <option value="offen">Offen</option>
              <option value="in_bearbeitung">In Bearbeitung</option>
              <option value="erledigt">Erledigt</option>
            </select>
          </div>
        </div>

        {/* KI-Agent Bereich komplett entfernt — keine KI-Erwähnungen für Mitarbeiter */}

        {/* Beschreibung */}
        <div>
          <label className="text-xs font-semibold uppercase tracking-wider text-gray-400 flex items-center gap-1.5 mb-1.5">
            <AlignLeft className="w-3 h-3" /> Beschreibung
          </label>
          {editingBeschreibung ? (
            <div>
              <textarea
                value={beschreibung}
                onChange={(e) => setBeschreibung(e.target.value)}
                onBlur={handleSaveBeschreibung}
                rows={4}
                className="w-full text-sm px-3 py-2 rounded-lg border border-gray-200 bg-gray-50 focus:outline-none focus:ring-2 focus:ring-[#B91C1C]/20 resize-none"
                placeholder="Beschreibung hinzufügen..."
                autoFocus
              />
              <div className="flex justify-end mt-1">
                <button
                  onClick={handleSaveBeschreibung}
                  className="text-xs font-medium text-[#B91C1C] px-2 py-1 rounded hover:bg-red-50"
                >
                  Speichern
                </button>
              </div>
            </div>
          ) : (
            <div
              onClick={() => setEditingBeschreibung(true)}
              className="text-sm text-gray-600 bg-gray-50 rounded-lg px-3 py-2 min-h-[60px] cursor-pointer hover:bg-gray-100 transition-colors"
            >
              {aufgabe.beschreibung || <span className="text-gray-400 italic">Beschreibung hinzufügen...</span>}
            </div>
          )}
        </div>

        {/* Checkliste */}
        <div>
          <label className="text-xs font-semibold uppercase tracking-wider text-gray-400 flex items-center gap-1.5 mb-2">
            <ListChecks className="w-3 h-3" /> Checkliste
            {checkliste.length > 0 && (
              <span className="text-gray-500 normal-case tracking-normal">
                ({checkliste.filter(c => c.done).length}/{checkliste.length})
              </span>
            )}
          </label>

          {/* Fortschrittsbalken */}
          {checkliste.length > 0 && (
            <div className="w-full h-1.5 bg-gray-100 rounded-full mb-3 overflow-hidden">
              <div
                className="h-full bg-green-500 rounded-full transition-all duration-300"
                style={{ width: `${(checkliste.filter(c => c.done).length / checkliste.length) * 100}%` }}
              />
            </div>
          )}

          {/* Items */}
          <div className="space-y-1">
            {checkliste.map((item) => (
              <div key={item.id} className="flex items-center gap-2 group">
                <button
                  onClick={() => handleToggleCheckItem(item.id)}
                  className="flex-shrink-0"
                >
                  {item.done ? (
                    <CheckCircle2 className="w-4 h-4 text-green-500" />
                  ) : (
                    <Circle className="w-4 h-4 text-gray-300 hover:text-[#B91C1C]" />
                  )}
                </button>
                <span className={`text-sm flex-1 ${item.done ? 'line-through text-gray-400' : 'text-gray-700'}`}>
                  {item.text}
                </span>
                <button
                  onClick={() => handleDeleteCheckItem(item.id)}
                  className="p-0.5 rounded hover:bg-gray-100 text-gray-300 opacity-0 group-hover:opacity-100 transition-opacity"
                >
                  <X className="w-3 h-3" />
                </button>
              </div>
            ))}
          </div>

          {/* Neues Item */}
          <div className="flex items-center gap-2 mt-2">
            <Plus className="w-4 h-4 text-gray-300 flex-shrink-0" />
            <input
              type="text"
              value={newCheckItem}
              onChange={(e) => setNewCheckItem(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') handleAddCheckItem(); }}
              placeholder="Punkt hinzufügen..."
              className="flex-1 text-sm border-none outline-none bg-transparent placeholder:text-gray-400"
            />
          </div>
        </div>

        {/* ===== LABELS / TAGS ===== */}
        <div>
          <label className="text-xs font-semibold uppercase tracking-wider text-gray-400 flex items-center gap-1.5 mb-2">
            <Tag className="w-3 h-3" /> Labels
          </label>
          {/* Zugeordnete Labels */}
          <div className="flex flex-wrap gap-1.5 mb-2">
            {labels.filter(l => zugeordneteLabels.includes(l.id)).map(label => (
              <span
                key={label.id}
                onClick={() => toggleLabel(label.id)}
                className="inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full cursor-pointer hover:opacity-80 transition-opacity"
                style={{ backgroundColor: label.farbe + '20', color: label.farbe, border: `1px solid ${label.farbe}40` }}
              >
                <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: label.farbe }} />
                {label.name}
                <X className="w-2.5 h-2.5 ml-0.5" />
              </span>
            ))}
            <button
              onClick={() => setShowLabelPicker(!showLabelPicker)}
              className="inline-flex items-center gap-1 text-xs font-medium text-gray-400 px-2 py-0.5 rounded-full border border-dashed border-gray-300 hover:border-gray-400 hover:text-gray-500 transition-colors"
            >
              <Plus className="w-2.5 h-2.5" /> Label
            </button>
          </div>
          {/* Label-Picker Dropdown */}
          {showLabelPicker && (
            <div className="bg-white border border-gray-200 rounded-lg shadow-lg p-2 space-y-1 mb-2">
              {labels.filter(l => !zugeordneteLabels.includes(l.id)).map(label => (
                <button
                  key={label.id}
                  onClick={() => { toggleLabel(label.id); setShowLabelPicker(false); }}
                  className="w-full flex items-center gap-2 text-xs px-2 py-1.5 rounded hover:bg-gray-50 transition-colors"
                >
                  <span className="w-3 h-3 rounded-full" style={{ backgroundColor: label.farbe }} />
                  {label.name}
                </button>
              ))}
              {labels.filter(l => !zugeordneteLabels.includes(l.id)).length === 0 && (
                <p className="text-xs text-gray-400 px-2 py-1">Keine weiteren Labels</p>
              )}
            </div>
          )}
        </div>

        {/* ===== UNTERAUFGABEN ===== */}
        <div>
          <label className="text-xs font-semibold uppercase tracking-wider text-gray-400 flex items-center gap-1.5 mb-2">
            <ListChecks className="w-3 h-3" /> Unteraufgaben
            {unteraufgaben.length > 0 && (
              <span className="text-gray-500 normal-case tracking-normal">
                ({unteraufgaben.filter(u => u.erledigt).length}/{unteraufgaben.length})
              </span>
            )}
          </label>
          {/* Fortschrittsbalken */}
          {unteraufgaben.length > 0 && (
            <div className="w-full h-1.5 bg-gray-100 rounded-full mb-3 overflow-hidden">
              <div
                className="h-full bg-blue-500 rounded-full transition-all duration-300"
                style={{ width: `${(unteraufgaben.filter(u => u.erledigt).length / unteraufgaben.length) * 100}%` }}
              />
            </div>
          )}
          <div className="space-y-1">
            {unteraufgaben.map((ua) => (
              <div key={ua.id} className="flex items-center gap-2 group">
                <button onClick={() => toggleUnteraufgabe(ua.id, ua.erledigt)} className="flex-shrink-0">
                  {ua.erledigt ? (
                    <CheckCircle2 className="w-4 h-4 text-blue-500" />
                  ) : (
                    <Circle className="w-4 h-4 text-gray-300 hover:text-blue-500" />
                  )}
                </button>
                <span className={`text-sm flex-1 ${ua.erledigt ? 'line-through text-gray-400' : 'text-gray-700'}`}>
                  {ua.titel}
                </span>
                <button
                  onClick={() => deleteUnteraufgabe(ua.id)}
                  className="p-0.5 rounded hover:bg-gray-100 text-gray-300 opacity-0 group-hover:opacity-100 transition-opacity"
                >
                  <X className="w-3 h-3" />
                </button>
              </div>
            ))}
          </div>
          <div className="flex items-center gap-2 mt-2">
            <Plus className="w-4 h-4 text-gray-300 flex-shrink-0" />
            <input
              type="text"
              value={newUnteraufgabe}
              onChange={(e) => setNewUnteraufgabe(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && newUnteraufgabe.trim()) {
                  addUnteraufgabe(newUnteraufgabe, myProfileId);
                  setNewUnteraufgabe('');
                }
              }}
              placeholder="Unteraufgabe hinzufügen..."
              className="flex-1 text-sm border-none outline-none bg-transparent placeholder:text-gray-400"
            />
          </div>
        </div>

        {/* ===== DATEIEN / DOKUMENTE ===== */}
        <div>
          <label className="text-xs font-semibold uppercase tracking-wider text-gray-400 flex items-center gap-1.5 mb-2">
            <Paperclip className="w-3 h-3" /> Dateien
            {dateien.length > 0 && (
              <span className="text-gray-500 normal-case tracking-normal">({dateien.length})</span>
            )}
          </label>
          {/* Drag & Drop Zone */}
          <div
            onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            onDrop={async (e) => {
              e.preventDefault();
              setDragOver(false);
              const files = Array.from(e.dataTransfer.files);
              for (const file of files) {
                const check = validateFileUpload(file);
                if (!check.valid) {
                  toast.error('Upload abgelehnt', { description: check.error });
                  continue;
                }
                await uploadDatei(file, myProfileId);
              }
            }}
            className={`border-2 border-dashed rounded-lg p-3 text-center transition-colors cursor-pointer ${
              dragOver ? 'border-[#B91C1C] bg-red-50' : 'border-gray-200 hover:border-gray-300'
            }`}
            onClick={() => fileInputRef.current?.click()}
          >
            <input
              ref={fileInputRef}
              type="file"
              multiple
              className="hidden"
              onChange={async (e) => {
                const files = Array.from(e.target.files || []);
                for (const file of files) {
                  const check = validateFileUpload(file);
                  if (!check.valid) {
                    toast.error('Upload abgelehnt', { description: check.error });
                    continue;
                  }
                  await uploadDatei(file, myProfileId);
                }
                if (fileInputRef.current) fileInputRef.current.value = '';
              }}
            />
            <Paperclip className="w-5 h-5 text-gray-300 mx-auto mb-1" />
            <p className="text-xs text-gray-400">
              {dragOver ? 'Dateien hier ablegen' : 'Klicken oder Dateien hierher ziehen (alle Formate, max. 50 MB)'}
            </p>
          </div>
          {/* Datei-Liste mit Bild-Miniaturvorschau */}
          {dateien.length > 0 && (
            <div className="mt-2 space-y-1">
              {/* Bild-Galerie: Miniaturvorschau für Bilder */}
              {dateien.filter(d => d.dateityp?.startsWith('image/')).length > 0 && (
                <div className="grid grid-cols-3 gap-2 mb-2">
                  {dateien.filter(d => d.dateityp?.startsWith('image/')).map(datei => (
                    <div key={datei.id} className="relative group rounded-lg overflow-hidden border border-gray-200 hover:border-gray-300 transition-colors">
                      <a href={datei.signedUrl || datei.dateipfad} target="_blank" rel="noopener noreferrer">
                        <img
                          src={datei.signedUrl || datei.dateipfad}
                          alt={datei.dateiname}
                          className="w-full h-20 object-cover cursor-pointer hover:opacity-90 transition-opacity"
                          loading="lazy"
                        />
                      </a>
                      <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/60 to-transparent px-1.5 py-1">
                        <p className="text-[9px] text-white truncate">{datei.dateiname}</p>
                      </div>
                      <button
                        onClick={() => deleteDatei(datei.id, datei.dateipfad)}
                        className="absolute top-1 right-1 p-0.5 rounded bg-black/40 text-white opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-500"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
              {/* Nicht-Bild-Dateien: klassische Liste */}
              {dateien.filter(d => !d.dateityp?.startsWith('image/')).map(datei => (
                <div key={datei.id} className="flex items-center gap-2 group px-2 py-1.5 rounded-lg hover:bg-gray-50 transition-colors">
                  {datei.dateityp?.includes('pdf') ? (
                    <FileText className="w-4 h-4 text-red-500 flex-shrink-0" />
                  ) : (
                    <File className="w-4 h-4 text-gray-400 flex-shrink-0" />
                  )}
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-medium text-gray-700 truncate">{datei.dateiname}</p>
                    <p className="text-[9px] text-gray-400">
                      {datei.dateigroesse < 1024 ? `${datei.dateigroesse} B` :
                       datei.dateigroesse < 1024 * 1024 ? `${(datei.dateigroesse / 1024).toFixed(1)} KB` :
                       `${(datei.dateigroesse / (1024 * 1024)).toFixed(1)} MB`}
                    </p>
                  </div>
                  <a
                    href={datei.signedUrl || datei.dateipfad}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="p-1 rounded hover:bg-gray-200 text-gray-400 hover:text-gray-600 transition-colors"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <Download className="w-3.5 h-3.5" />
                  </a>
                  <button
                    onClick={() => deleteDatei(datei.id, datei.dateipfad)}
                    className="p-1 rounded hover:bg-red-50 text-gray-300 opacity-0 group-hover:opacity-100 transition-opacity hover:text-red-500"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Metadaten Footer */}
        <div className="pt-3 border-t border-gray-100 space-y-1">
          {aufgabe.ersteller && (
            <p className="text-xs text-gray-400">
              Erstellt von {aufgabe.ersteller.vorname} {aufgabe.ersteller.nachname}
            </p>
          )}
          <p className="text-xs text-gray-400">
            Erstellt: {new Date(aufgabe.created_at).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
          </p>
          {aufgabe.erledigt_am && (
            <p className="text-xs text-green-500">
              Erledigt: {new Date(aufgabe.erledigt_am).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
            </p>
          )}
        </div>

        {/* Footer: Löschen */}
        <div className="pt-2">
          <button
            onClick={handleDelete}
            className="flex items-center gap-1.5 text-xs font-medium text-red-500 hover:text-red-700 transition-colors"
          >
            <Trash2 className="w-3.5 h-3.5" />
            Aufgabe löschen
          </button>
        </div>
      </div>

      {/* ===== STICKY CHAT / KOMMENTARE (WhatsApp-Style) ===== */}
      <StickyChat
        kommentare={kommentare}
        addKommentar={addKommentar}
        deleteKommentar={deleteKommentar}
        uploadDatei={uploadDatei}
        myProfileId={myProfileId}
        mitarbeiterList={mitarbeiterList}
        aufgabeId={aufgabe.id}
        aufgabeTitel={aufgabe.titel}
      />
    </aside>
  );
}

// ============================================================
// STICKY CHAT / KOMMENTARE (WhatsApp-Style)
// ============================================================
function StickyChat({ kommentare, addKommentar, deleteKommentar, uploadDatei, myProfileId, mitarbeiterList, aufgabeId, aufgabeTitel }: {
  kommentare: AufgabeKommentar[];
  addKommentar: (inhalt: string, autorId: string) => Promise<boolean>;
  deleteKommentar: (id: string) => Promise<boolean>;
  uploadDatei: (file: File, userId: string) => Promise<any>;
  myProfileId: string;
  mitarbeiterList: any[];
  aufgabeId: string;
  aufgabeTitel: string;
}) {
  const [expanded, setExpanded] = useState(true);
  const [message, setMessage] = useState('');
  const [showMentions, setShowMentions] = useState(false);
  const [mentionFilter, setMentionFilter] = useState('');
  const [mentionIndex, setMentionIndex] = useState(0);
  const [pastedImages, setPastedImages] = useState<{ file: File; preview: string }[]>([]);
  const chatEndRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const chatFileInputRef = useRef<HTMLInputElement>(null);

  // Auto-scroll to bottom when new messages
  useEffect(() => {
    if (expanded && chatEndRef.current) {
      chatEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [kommentare.length, expanded]);

  // Filtered mitarbeiter for @mentions
  const filteredMitarbeiter = useMemo(() => {
    return (mitarbeiterList || []).filter((m: any) =>
      m.ist_aktiv && m.id !== myProfileId &&
      (`${m.vorname} ${m.nachname}`).toLowerCase().includes(mentionFilter.toLowerCase())
    );
  }, [mitarbeiterList, mentionFilter, myProfileId]);

  // Handle @mention detection in textarea
  const handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const val = e.target.value;
    setMessage(val);

    // Detect @ at cursor position
    const cursorPos = e.target.selectionStart || 0;
    const textBeforeCursor = val.substring(0, cursorPos);
    const atMatch = textBeforeCursor.match(/@([\wÀ-ɏ]*)$/);
    if (atMatch) {
      setShowMentions(true);
      setMentionFilter(atMatch[1]);
      setMentionIndex(0);
    } else {
      setShowMentions(false);
      setMentionFilter('');
    }
  };

  // Insert @mention
  const insertMention = (m: any) => {
    const cursorPos = textareaRef.current?.selectionStart || 0;
    const textBeforeCursor = message.substring(0, cursorPos);
    const atPos = textBeforeCursor.lastIndexOf('@');
    const textAfterCursor = message.substring(cursorPos);
    const mentionText = `@${m.vorname} ${m.nachname} `;
    const newMsg = textBeforeCursor.substring(0, atPos) + mentionText + textAfterCursor;
    setMessage(newMsg);
    setShowMentions(false);
    setMentionFilter('');
    setTimeout(() => textareaRef.current?.focus(), 50);
  };

  // Handle Strg+V for image paste
  const handlePaste = (e: React.ClipboardEvent) => {
    const items = e.clipboardData?.items;
    if (!items) return;
    for (let i = 0; i < items.length; i++) {
      if (items[i].type.startsWith('image/')) {
        e.preventDefault();
        const file = items[i].getAsFile();
        if (file) {
          const preview = URL.createObjectURL(file);
          setPastedImages(prev => [...prev, { file, preview }]);
        }
      }
    }
  };

  // Remove pasted image
  const removePastedImage = (idx: number) => {
    setPastedImages(prev => {
      URL.revokeObjectURL(prev[idx].preview);
      return prev.filter((_, i) => i !== idx);
    });
  };

  // Send message with @mention notifications
  const handleSend = async () => {
    if (!message.trim() && pastedImages.length === 0) return;

    // Upload pasted images first
    for (const img of pastedImages) {
      await uploadDatei(img.file, myProfileId);
      URL.revokeObjectURL(img.preview);
    }
    setPastedImages([]);

    // Send text comment
    if (message.trim()) {
      await addKommentar(message, myProfileId);

      // Find @mentions and send notifications
      const mentionRegex = /@([\wÀ-ɏ]+\s[\wÀ-ɏ]+)/g;
      let match;
      const mentionedIds: string[] = [];
      while ((match = mentionRegex.exec(message)) !== null) {
        const fullName = match[1];
        const found = (mitarbeiterList || []).find((m: any) =>
          `${m.vorname} ${m.nachname}` === fullName
        );
        if (found && found.id !== myProfileId) {
          mentionedIds.push(found.id);
        }
      }

      // Create notifications for mentioned users
      if (mentionedIds.length > 0) {
        const myName = (mitarbeiterList || []).find((m: any) => m.id === myProfileId);
        const senderName = myName ? `${myName.vorname} ${myName.nachname}` : 'Jemand';
        const notifications = mentionedIds.map(mid => ({
          mitarbeiter_id: mid,
          type: 'aufgabe',
          title: `${senderName} hat dich erwähnt`,
          message: `In Aufgabe "${aufgabeTitel}": ${message.substring(0, 100)}`,
          link: `/aufgaben?task=${aufgabeId}`,
          is_read: false,
        }));
        try {
          // supabase bereits importiert
          await supabase.from('notifications').insert(notifications);
        } catch (err) {
          console.error('Notification fehlgeschlagen:', err);
        }
      }
    }

    setMessage('');
  };

  // Handle keyboard in textarea
  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (showMentions) {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setMentionIndex(prev => Math.min(prev + 1, filteredMitarbeiter.length - 1));
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setMentionIndex(prev => Math.max(prev - 1, 0));
      } else if (e.key === 'Enter' && filteredMitarbeiter[mentionIndex]) {
        e.preventDefault();
        insertMention(filteredMitarbeiter[mentionIndex]);
      } else if (e.key === 'Escape') {
        setShowMentions(false);
      }
    } else if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  // Render comment content with highlighted @mentions
  const renderContent = (text: string) => {
    const parts = text.split(/(@[\wÀ-ɏ]+\s[\wÀ-ɏ]+)/);
    return parts.map((part, i) => {
      if (part.startsWith('@')) {
        return <span key={i} className="text-[#B91C1C] font-semibold">{part}</span>;
      }
      return <span key={i}>{part}</span>;
    });
  };

  return (
    <div className="flex flex-col border-t border-gray-200 bg-white" style={{ minHeight: expanded ? '240px' : '44px', maxHeight: expanded ? '50%' : '44px', transition: 'all 0.2s ease' }}>
      {/* Chat Header - always visible, click to toggle */}
      <button
        onClick={() => setExpanded(!expanded)}
        className="flex items-center justify-between px-4 py-2.5 bg-gray-50 hover:bg-gray-100 transition-colors flex-shrink-0"
      >
        <div className="flex items-center gap-2">
          <MessageSquare className="w-3.5 h-3.5 text-gray-500" />
          <span className="text-xs font-semibold text-gray-600 uppercase tracking-wider">Kommentare</span>
          {kommentare.length > 0 && (
            <span className="text-[10px] bg-[#B91C1C] text-white rounded-full px-1.5 py-0.5 font-bold min-w-[18px] text-center">
              {kommentare.length}
            </span>
          )}
        </div>
        {expanded ? <ChevronDown className="w-4 h-4 text-gray-400" /> : <ChevronUp className="w-4 h-4 text-gray-400" />}
      </button>

      {expanded && (
        <>
          {/* Chat Messages (scrollable) */}
          <div className="flex-1 overflow-y-auto px-3 py-2 space-y-2" style={{ scrollbarWidth: 'thin' }}>
            {kommentare.length === 0 && (
              <div className="flex items-center justify-center py-6">
                <p className="text-xs text-gray-400">Noch keine Kommentare. Schreibe den ersten!</p>
              </div>
            )}
            {kommentare.map(k => {
              const isMe = k.autor_id === myProfileId;
              return (
                <div key={k.id} className={`flex ${isMe ? 'justify-end' : 'justify-start'}`}>
                  <div className={`max-w-[85%] group ${isMe ? 'order-1' : ''}`}>
                    {/* Author name (only for others) */}
                    {!isMe && (
                      <p className="text-[10px] font-semibold text-gray-500 mb-0.5 ml-1">
                        {k.autor ? `${k.autor.vorname} ${k.autor.nachname}` : 'Unbekannt'}
                      </p>
                    )}
                    <div className={`rounded-2xl px-3 py-2 relative ${
                      isMe
                        ? 'bg-[#B91C1C] text-white rounded-br-md'
                        : 'bg-gray-100 text-gray-800 rounded-bl-md'
                    }`}>
                      <p className="text-xs whitespace-pre-wrap break-words">{renderContent(k.inhalt)}</p>
                      <div className={`flex items-center gap-1 mt-1 ${isMe ? 'justify-end' : 'justify-start'}`}>
                        <p className={`text-[9px] ${isMe ? 'text-white/60' : 'text-gray-400'}`}>
                          {new Date(k.created_at).toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' })}
                        </p>
                        <button
                          onClick={() => deleteKommentar(k.id)}
                          className={`p-0.5 rounded opacity-0 group-hover:opacity-100 transition-opacity ${
                            isMe ? 'hover:bg-white/20 text-white/60' : 'hover:bg-red-50 text-gray-300 hover:text-red-500'
                          }`}
                        >
                          <X className="w-2.5 h-2.5" />
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
            <div ref={chatEndRef} />
          </div>

          {/* Pasted image previews */}
          {pastedImages.length > 0 && (
            <div className="flex gap-2 px-3 py-2 border-t border-gray-100 bg-gray-50">
              {pastedImages.map((img, idx) => (
                <div key={idx} className="relative w-16 h-16 rounded-lg overflow-hidden border border-gray-200">
                  <img src={img.preview} alt="Paste" className="w-full h-full object-cover" />
                  <button
                    onClick={() => removePastedImage(idx)}
                    className="absolute top-0.5 right-0.5 p-0.5 rounded-full bg-black/50 text-white hover:bg-red-500"
                  >
                    <X className="w-2.5 h-2.5" />
                  </button>
                </div>
              ))}
            </div>
          )}

          {/* @Mention Dropdown */}
          {showMentions && filteredMitarbeiter.length > 0 && (
            <div className="mx-3 mb-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-32 overflow-y-auto">
              {filteredMitarbeiter.slice(0, 6).map((m: any, idx: number) => (
                <button
                  key={m.id}
                  onClick={() => insertMention(m)}
                  className={`w-full flex items-center gap-2 px-3 py-2 text-left text-xs hover:bg-gray-50 transition-colors ${
                    idx === mentionIndex ? 'bg-gray-100' : ''
                  }`}
                >
                  <div className="w-6 h-6 rounded-full bg-[#B91C1C] text-white flex items-center justify-center text-[9px] font-bold flex-shrink-0">
                    {m.initialen || `${(m.vorname || '')[0]}${(m.nachname || '')[0]}`}
                  </div>
                  <span className="font-medium text-gray-700">{m.vorname} {m.nachname}</span>
                  <span className="text-gray-400 text-[10px]">{m.rolle}</span>
                </button>
              ))}
            </div>
          )}

          {/* Input Bar (sticky bottom) */}
          <div className="flex items-end gap-2 px-3 py-2 border-t border-gray-200 bg-white flex-shrink-0">
            <input
              ref={chatFileInputRef}
              type="file"
              accept="image/*"
              className="hidden"
              onChange={async (e) => {
                const files = Array.from(e.target.files || []);
                for (const file of files) {
                  if (file.type.startsWith('image/')) {
                    const preview = URL.createObjectURL(file);
                    setPastedImages(prev => [...prev, { file, preview }]);
                  } else {
                    await uploadDatei(file, myProfileId);
                  }
                }
                if (chatFileInputRef.current) chatFileInputRef.current.value = '';
              }}
            />
            <button
              onClick={() => chatFileInputRef.current?.click()}
              className="p-2 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors flex-shrink-0"
              title="Bild anhängen"
            >
              <Paperclip className="w-4 h-4" />
            </button>
            <div className="flex-1 relative">
              <textarea
                ref={textareaRef}
                value={message}
                onChange={handleInputChange}
                onKeyDown={handleKeyDown}
                onPaste={handlePaste}
                placeholder="Nachricht schreiben... (@Name für Erwähnung)"
                rows={1}
                className="w-full text-sm px-3 py-2 rounded-2xl border border-gray-200 bg-gray-50 focus:outline-none focus:ring-2 focus:ring-[#B91C1C]/20 resize-none"
                style={{ maxHeight: '80px', minHeight: '36px' }}
                onInput={(e) => {
                  const el = e.target as HTMLTextAreaElement;
                  el.style.height = 'auto';
                  el.style.height = Math.min(el.scrollHeight, 80) + 'px';
                }}
              />
            </div>
            <button
              onClick={handleSend}
              disabled={!message.trim() && pastedImages.length === 0}
              className="p-2 rounded-full bg-[#B91C1C] text-white hover:bg-[#991B1B] disabled:opacity-30 disabled:cursor-not-allowed transition-colors flex-shrink-0"
            >
              <Send className="w-4 h-4" />
            </button>
          </div>
        </>
      )}
    </div>
  );
}

// ============================================================
// WIEDERHOLUNG-SECTION (Detail-Panel)
// ============================================================
function WiederholungSection({ aufgabe, onUpdate }: {
  aufgabe: AufgabeMitMitarbeiter;
  onUpdate: (id: string, data: any) => Promise<boolean>;
}) {
  const [expanded, setExpanded] = useState(false);
  const aktuellerTyp = (aufgabe.wiederholung_typ || '') as WiederholungTyp | '';
  const wochentage = aufgabe.wiederholung_wochentage || [];
  const intervall = aufgabe.wiederholung_intervall || 1;

  const handleTypChange = async (typ: string) => {
    if (!typ) {
      // Wiederholung entfernen
      await onUpdate(aufgabe.id, {
        wiederholung_typ: null,
        wiederholung_intervall: null,
        wiederholung_wochentage: null,
        wiederholung_ende: null,
        naechste_faelligkeit: null,
      });
    } else {
      const updates: any = { wiederholung_typ: typ };
      if (typ === 'benutzerdefiniert') {
        updates.wiederholung_intervall = intervall || 1;
      }
      if (typ === 'woechentlich' && wochentage.length === 0) {
        // Standard: aktueller Wochentag
        const heute = new Date().getDay();
        updates.wiederholung_wochentage = [heute];
      }
      await onUpdate(aufgabe.id, updates);
    }
  };

  const toggleWochentag = async (tag: number) => {
    const neu = wochentage.includes(tag)
      ? wochentage.filter(t => t !== tag)
      : [...wochentage, tag].sort((a, b) => a - b);
    if (neu.length === 0) return; // Mindestens ein Tag muss ausgewählt sein
    await onUpdate(aufgabe.id, { wiederholung_wochentage: neu });
  };

  const handleIntervallChange = async (neuesIntervall: number) => {
    if (neuesIntervall < 1) return;
    await onUpdate(aufgabe.id, { wiederholung_intervall: neuesIntervall });
  };

  const handleEndeChange = async (datum: string) => {
    await onUpdate(aufgabe.id, {
      wiederholung_ende: datum ? new Date(datum).toISOString() : null,
    });
  };

  return (
    <div>
      <label className="text-xs font-semibold uppercase tracking-wider text-gray-400 flex items-center gap-1.5 mb-1.5">
        <Repeat className="w-3 h-3" /> Wiederholung
      </label>

      {/* Wiederholungs-Typ Auswahl */}
      <div className="flex flex-wrap gap-1.5 mb-2">
        <button
          onClick={() => handleTypChange('')}
          className={`text-xs px-2.5 py-1 rounded-lg border transition-colors ${
            !aktuellerTyp
              ? 'bg-gray-900 text-white border-gray-900'
              : 'bg-gray-50 text-gray-600 border-gray-200 hover:bg-gray-100'
          }`}
        >
          Keine
        </button>
        {(Object.entries(WIEDERHOLUNG_LABELS) as [WiederholungTyp, string][]).map(([key, label]) => (
          <button
            key={key}
            onClick={() => handleTypChange(key)}
            className={`text-xs px-2.5 py-1 rounded-lg border transition-colors ${
              aktuellerTyp === key
                ? 'bg-purple-600 text-white border-purple-600'
                : 'bg-gray-50 text-gray-600 border-gray-200 hover:bg-purple-50 hover:border-purple-300'
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      {/* Erweiterte Einstellungen */}
      {aktuellerTyp && (
        <div className="bg-purple-50/50 rounded-lg border border-purple-100 p-3 space-y-3">
          {/* Wöchentlich: Wochentage auswählen */}
          {aktuellerTyp === 'woechentlich' && (
            <div>
              <p className="text-xs font-medium text-gray-500 mb-1.5">An welchen Tagen?</p>
              <div className="flex gap-1">
                {[1, 2, 3, 4, 5, 6, 0].map((tag) => (
                  <button
                    key={tag}
                    onClick={() => toggleWochentag(tag)}
                    className={`w-8 h-8 text-xs font-semibold rounded-full border transition-colors ${
                      wochentage.includes(tag)
                        ? 'bg-purple-600 text-white border-purple-600'
                        : 'bg-white text-gray-500 border-gray-200 hover:bg-purple-50'
                    }`}
                  >
                    {WOCHENTAG_LABELS[tag]}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Benutzerdefiniert: Intervall in Tagen */}
          {aktuellerTyp === 'benutzerdefiniert' && (
            <div>
              <p className="text-xs font-medium text-gray-500 mb-1.5">Alle wie viele Tage?</p>
              <div className="flex items-center gap-2">
                <span className="text-xs text-gray-500">Alle</span>
                <input
                  type="number"
                  min={1}
                  max={365}
                  value={intervall}
                  onChange={(e) => handleIntervallChange(parseInt(e.target.value) || 1)}
                  className="w-16 text-sm text-center px-2 py-1 rounded-lg border border-gray-200 bg-white focus:outline-none focus:ring-2 focus:ring-purple-300 focus:border-purple-400"
                />
                <span className="text-xs text-gray-500">Tage</span>
              </div>
            </div>
          )}

          {/* Enddatum (optional, für alle Typen) */}
          <div>
            <p className="text-xs font-medium text-gray-500 mb-1.5">Wiederholung endet am (optional)</p>
            <input
              type="date"
              value={aufgabe.wiederholung_ende ? new Date(aufgabe.wiederholung_ende).toISOString().split('T')[0] : ''}
              onChange={(e) => handleEndeChange(e.target.value)}
              className="w-full text-sm px-3 py-1.5 rounded-lg border border-gray-200 bg-white focus:outline-none focus:ring-2 focus:ring-purple-300 focus:border-purple-400"
            />
          </div>

          {/* Zusammenfassung */}
          <div className="flex items-center gap-2 pt-1 border-t border-purple-100">
            <RefreshCw className="w-3.5 h-3.5 text-purple-500" />
            <p className="text-xs text-purple-600 font-medium">
              {aktuellerTyp === 'taeglich' && 'Wiederholt sich jeden Tag'}
              {aktuellerTyp === 'woechentlich' && `Jeden ${wochentage.map(t => WOCHENTAG_LABELS[t]).join(', ')}`}
              {aktuellerTyp === 'monatlich' && 'Jeden Monat am gleichen Tag'}
              {aktuellerTyp === 'jaehrlich' && 'Jedes Jahr am gleichen Datum'}
              {aktuellerTyp === 'benutzerdefiniert' && `Alle ${intervall} Tage`}
              {aufgabe.wiederholung_ende && (
                <span className="text-gray-400 font-normal"> — bis {new Date(aufgabe.wiederholung_ende).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' })}</span>
              )}
            </p>
          </div>
        </div>
      )}
    </div>
  );
}
